import { loadWorkspace } from '@shared/storage';
import { Workspace } from '@shared/types';
import { SyncEngine, SyncResult, SyncStatus } from './types';
import { cloudSyncEngine } from './cloudEngine';

const listeners = new Set<() => void>();

let currentStatus: SyncStatus = {
  mode: 'local-only',
  phase: 'idle',
  label: 'Local',
  message: 'Mode local actif.',
  source: 'local',
};

function emit() {
  listeners.forEach((listener) => listener());
}

function setStatus(next: SyncStatus) {
  currentStatus = next;
  emit();
}

function buildSuccessStatus(message: string): SyncStatus {
  return {
    mode: 'local-only',
    phase: 'synced',
    label: 'Local',
    message,
    source: 'local',
    lastSyncAt: Date.now(),
  };
}

export const localSyncEngine: SyncEngine = {
  getStatus: () => currentStatus,

  initialize: (workspace: Workspace) => {
    setStatus({
      ...buildSuccessStatus('Donnees locales pretes.'),
      lastSyncAt: workspace.meta.updatedAt,
    });
  },

  push: async (workspace: Workspace): Promise<SyncResult> => {
    setStatus({
      mode: 'local-only',
      phase: 'syncing',
      label: 'Local',
      message: 'Mise a jour locale en cours.',
      source: 'local',
      lastSyncAt: currentStatus.lastSyncAt,
    });

    const status = {
      ...buildSuccessStatus('Donnees locales a jour.'),
      lastSyncAt: workspace.meta.updatedAt,
    };
    setStatus(status);

    return {
      ok: true,
      status,
      workspace,
    };
  },

  pull: async (): Promise<SyncResult> => {
    const workspace = loadWorkspace();
    const status = {
      ...buildSuccessStatus('Derniere version locale rechargee.'),
      lastSyncAt: workspace.meta.updatedAt,
    };
    setStatus(status);

    return {
      ok: true,
      status,
      workspace,
    };
  },

  sync: async (workspace: Workspace): Promise<SyncResult> => localSyncEngine.push(workspace),

  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function getSyncEngine() {
  try {
    const w = window as any;
    const isPro = typeof w.IndxLicense?.isPro === 'function' ? w.IndxLicense.isPro() : false;
    return isPro ? cloudSyncEngine : localSyncEngine;
  } catch {
    return localSyncEngine;
  }
}
